import { CategoryBarChart, F0Box, F0Heading, F0Text } from '@factorialco/f0-react'

import { OverviewSummariesDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

interface Props {
  periodId: string
  currency: string
}

const formatMoney = (amountInCents: string, currency: string): string => {
  const amount = Number(amountInCents) / 100

  if (isNaN(amount)) return '0'

  try {
    return new Intl.NumberFormat(i18n.locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return String(amount)
  }
}

const OverviewSummaryCards = ({ periodId, currency }: Props) => {
  const { data, loading } = useQuery(OverviewSummariesDocument, {
    variables: { planningPeriodId: periodId },
  })

  if (loading || !data) return null

  const summary = data.planning.headcountSummariesConnection.nodes[0]

  if (!summary) return null

  const currentHeadcount = summary.actualHeadcountCount
  const forecastedHeadcount = summary.actualHeadcountCount + summary.newHeadcountCount
  const currentCost = summary.actualHeadcountTotalMoney
  const forecastedCost =
    Number(summary.actualHeadcountTotalMoney) + Number(summary.newHeadcountTotalMoney)

  return (
    <F0Box display="flex" gap="md">
      <F0Box
        display="flex"
        flexDirection="column"
        gap="sm"
        padding="lg"
        borderRadius="md"
        border="default"
        width="full"
      >
        <F0Text
          content={i18n.t('planning.overview.current_forecasted')}
          variant="label"
          color="secondary"
        />
        <F0Box display="flex" alignItems="baseline" gap="xs">
          <F0Heading content={String(currentHeadcount)} variant="heading-large" />
          <F0Heading content={`/ ${forecastedHeadcount}`} variant="heading" color="secondary" />
        </F0Box>
        <CategoryBarChart
          data={[
            {
              name: i18n.t('planning.employee_sheet.actual_headcount'),
              value: currentHeadcount,
              color: 'categorical-1',
            },
            {
              name: i18n.t('planning.employee_sheet.new_headcount'),
              value: summary.newHeadcountCount,
              color: 'categorical-5',
            },
          ]}
          legend
        />
      </F0Box>

      <F0Box
        display="flex"
        flexDirection="column"
        gap="sm"
        padding="lg"
        borderRadius="md"
        border="default"
        width="full"
      >
        <F0Text
          content={i18n.t('planning.overview.forecasted_cost')}
          variant="label"
          color="secondary"
        />
        <F0Heading
          content={formatMoney(String(forecastedCost), currency)}
          variant="heading-large"
        />
        <CategoryBarChart
          data={[
            {
              name: `${i18n.t('planning.employee_sheet.actual_headcount')}: ${formatMoney(currentCost, currency)}`,
              value: Number(currentCost),
              color: 'categorical-1',
            },
            {
              name: `${i18n.t('planning.employee_sheet.new_headcount')}: ${formatMoney(summary.newHeadcountTotalMoney, currency)}`,
              value: Number(summary.newHeadcountTotalMoney),
              color: 'categorical-5',
            },
          ]}
          legend
        />
      </F0Box>
    </F0Box>
  )
}

export default OverviewSummaryCards
